import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import Seo, { breadcrumbLd, ogUrl } from "@/components/site/Seo";
import { guideBySlug } from "@/data/guides";
import { ProgressBar, RelatedStrip } from "@/components/site/conversion";
import { track } from "@/lib/analytics";
import { useCurrency, money } from "@/lib/currency";
import { API } from "@/lib/api";
import { BookCover } from "@/components/site/PublicPresentation";
import { BookOpen, ArrowUpRight, ThumbsUp, ThumbsDown, ZoomIn } from "lucide-react";
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BASE = "https://thesourcingatlas.com";

function ShareButtons({ title, slug }) {
  const url = `${BASE}/guides/${slug}`;
  const enc = encodeURIComponent;
  const links = [
    ["WhatsApp", `https://wa.me/?text=${enc(title + " — " + url)}`],
    ["LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`],
    ["X", `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`],
    ["Telegram", `https://t.me/share/url?url=${enc(url)}&text=${enc(title)}`],
  ];
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }} data-testid="share-buttons">
      {links.map(([n, h]) => (
        <a key={n} data-testid={`share-${n.toLowerCase()}`} className="btn btn-line" style={{ padding: "8px 14px", fontSize: 12.5 }} href={h} target="_blank" rel="noreferrer">{n}</a>
      ))}
      <button className="btn btn-line" data-testid="share-copy-link" style={{ padding: "8px 14px", fontSize: 12.5 }} onClick={() => navigator.clipboard?.writeText(url)}>Copy link</button>
    </div>
  );
}

export default function GuideDetail() {
  const { slug } = useParams();
  const { currency } = useCurrency();
  const [cat, setCat] = useState(null);

  useEffect(() => {
    fetch(`${API}/guides`, { credentials: "include" }).then((r) => r.json()).then(setCat).catch(() => {});
  }, [currency]);

  const stat = guideBySlug(slug);
  const apiGuide = cat?.guides?.find((x) => x.slug === slug);
  const g = apiGuide || stat;
  if (cat && !apiGuide && !stat) return <Navigate to="/guides" replace />;
  if (!g) return <SiteLayout><div className="simplepage"><p>Loading…</p></div></SiteLayout>;

  const toc = (stat && stat.toc) || g.toc || [];
  const summary = g.summary || (stat && stat.summary) || g.blurb || "";
  const isPaid = g.tag === "paid";
  const crossBundle = isPaid && cat ? cat.bundles.find((b) => (b.guide_slugs || []).includes(slug)) : null;

  const productLd = isPaid && Number.isFinite(g.price) ? {
    "@context": "https://schema.org", "@type": "Product", name: g.title, description: summary,
    image: ogUrl(g.title, "Premium Sourcing Guide"),
    offers: { "@type": "Offer", url: `${BASE}/guides/${slug}`, price: g.price, priceCurrency: currency === "inr" ? "INR" : "USD", availability: "https://schema.org/InStock" },
  } : null;
  const articleLd = {
    "@context": "https://schema.org", "@type": "Article", headline: g.title,
    description: summary,
    ...(g.published_at ? { datePublished: g.published_at } : {}),
    ...(g.updated_at ? { dateModified: g.updated_at } : {}),
    image: ogUrl(g.title, isPaid ? "Premium Sourcing Guide" : "Free Sourcing Guide"),
    mainEntityOfPage: `${BASE}/guides/${slug}`,
    author: { "@type": "Organization", name: "The Sourcing Atlas" },
  };

  return (
    <SiteLayout>
      <ProgressBar />
      <Seo
        title={`${g.title} — Sourcing Guide | The Sourcing Atlas`}
        description={summary.slice(0, 158)}
        path={`/guides/${slug}`}
        ogType="article"
        ogImage={ogUrl(g.title, g.tag === "free" ? "Free Guide" : "Field Manual")}
        jsonLd={[
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Guides", path: "/guides" }, { name: g.title, path: `/guides/${slug}` }]),
          articleLd, ...(productLd ? [productLd] : []),
        ]}
      />
      <div className="crumb"><Link to="/">Home</Link> › <Link to="/guides">Guides</Link> › {g.title}</div>
      <div className="simplepage at-guide-detail" data-testid="guide-detail">
        <aside><BookCover guide={g} prefix="detail" large /><div className="at-guide-format"><BookOpen size={14} /> DIGITAL PUBLICATION · PDF EDITION</div><Dialog><DialogTrigger asChild><button className="btn btn-line" style={{ marginTop: 18, width: "100%" }} data-testid="guide-cover-preview"><ZoomIn size={15} />Preview the cover</button></DialogTrigger><DialogContent data-testid="guide-preview-modal" className="atlas-public z-[220] max-h-[90dvh] overflow-auto"><DialogTitle>{g.title}</DialogTitle><DialogDescription>Publication cover preview. Full guide access follows the existing download or purchase process.</DialogDescription><img src={`${API}/guides/${slug}/thumb.png`} alt={`${g.title} cover preview`} style={{ maxHeight: "65vh", margin: "auto", objectFit: "contain" }} data-testid="guide-cover-preview-image" /></DialogContent></Dialog></aside>
        <div>
        <span className={"at-guide-badge " + (g.tag === "free" ? "free" : "premium")} data-testid="guide-access-badge">
          {g.tag === "free" ? "FREE ATLAS" : "PREMIUM EDITION"}
        </span>
        <h1 data-testid="guide-title">{g.title}</h1>
        <p style={{ color: "var(--grey)", fontSize: 16 }}>{summary}</p>

        {toc.length > 0 && <><h2>What's inside</h2><ul>{toc.map((t, i) => <li key={i}>{t}</li>)}</ul></>}

        <h2>Overview</h2>
        <p>
          Explore the topics and overview of {g.title} before choosing your digital edition.
          Importers use our guides to plan buying trips, understand manufacturing clusters and
          ask better questions before committing to suppliers.
        </p>
        <p>
          The Sourcing Atlas is an independent Asian sourcing & export house that sources from
          China, India, Vietnam and Thailand.
        </p>

        <div style={{ marginTop: 24, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {g.tag === "free" && <Link className="btn btn-red" to="/guides" data-testid="guide-download-cta">Get the PDF by email</Link>}
          {isPaid && <Link className="btn btn-navy" to={`/checkout/guide/${slug}`} data-testid="guide-buy-cta">{Number.isFinite(g.price) ? `Get the guide · ${money(currency, g.price)}` : "View pricing"}<ArrowUpRight size={16} /></Link>}
          <Link className="btn btn-line" to="/contact" data-testid="guide-contact-cta">Talk to the team</Link>
        </div>

        {crossBundle && (
          <div data-testid="cross-sell-bundle" style={{ marginTop: 26, background: "#0B2649", color: "#fff", borderRadius: 14, padding: 22 }}>
            <div className="fc-k" style={{ color: "#E8C67A" }}>SAVE WITH A BUNDLE</div>
            <h3 style={{ color: "#fff", margin: "4px 0 6px" }}>Get this inside the {crossBundle.name}</h3>
            <p style={{ color: "#CFE0F4", fontSize: 14 }}>
              {crossBundle.guide_slugs.length} guides for {money(currency, crossBundle.price)}
              {crossBundle.was > crossBundle.price && <span style={{ textDecoration: "line-through", opacity: .6, marginLeft: 8 }}>{money(currency, crossBundle.was)}</span>}
            </p>
            <Link className="btn btn-wht" to={`/checkout/bundle/${crossBundle.slug}`} style={{ marginTop: 8 }} data-testid="cross-sell-cta">Get the {crossBundle.name} →</Link>
          </div>
        )}

    <div style={{ marginTop: 14 }}>
      <Link to="/services#verification" data-testid="guide-related-service" className="at-text-link">Need hands-on verification support?<ArrowUpRight size={16} /></Link>
    </div>
        <ShareButtons title={g.title} slug={slug} />
        <div className="thumbs" data-testid="guide-thumbs">
          <span style={{ fontSize: 13, color: "var(--grey)" }}>Was this useful?</span>
          <button title="Yes" onClick={() => { track("guide_feedback", { slug, v: 1 }); alert("Thanks for the feedback!"); }} data-testid="thumb-up"><ThumbsUp size={17} /></button>
          <button title="No" onClick={() => { track("guide_feedback", { slug, v: -1 }); alert("Thanks — we'll improve it."); }} data-testid="thumb-down"><ThumbsDown size={17} /></button>
        </div>
        </div>
      </div>
      <RelatedStrip items={[
        { kicker: "Guides", title: "Costly sourcing mistakes", to: "/risks" },
        { kicker: "Library", title: "All guides & bundles", to: "/guides" },
        { kicker: "Tours", title: "Canton Fair tours", to: "/tours" },
      ]} />
    </SiteLayout>
  );
}
